import { onMounted } from 'vue'
import useGet from '@/core/composables/api/useGet'
import useDelete from '@/core/composables/api/useDelete'
import type { Member } from '@/core/models/member/member'
import type { Perspective } from '@/core/models/perspective'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { useI18n } from 'vue-i18n'
import { useSnackStore } from '@/stores/snack.store'
import { useRouter } from 'vue-router'

/** Nested topic on list + detail goal payloads — CMS perspectives, self via includeSelf. */
export interface CoachFeedbackGoalTopic {
  readonly id: string
  readonly title: string
  readonly icon?: string
  readonly perspectives: ReadonlyArray<Perspective>
  readonly includeSelf: boolean
}

export const useCoachFeedbackGoal = (feedbackGoalId: string) => {
  const { t } = useI18n()
  const snackStore = useSnackStore()
  const router = useRouter()

  const { data, status, load, reload } = useGet<CoachFeedbackGoal>(
    `coaching/feedback-goals/${feedbackGoalId}`,
  )
  const { remove, status: deleteStatus } = useDelete<void>(`coaching/feedback-goals/${feedbackGoalId}`)

  onMounted(() => {
    load()
  })

  const deleteGoal = async () => {
    await remove({})
    switch (deleteStatus.value) {
      case ApiRequestStatus.Success:
        snackStore.showInfo(t('coaching.feedbackGoals.deleteSuccess'))
        await router.push({ name: 'coach-feedback-goals' })
        break
      case ApiRequestStatus.Error:
        snackStore.showError(t('coaching.feedbackGoals.deleteError'))
        break
    }
  }

  return {
    feedbackGoal: data,
    status,
    reload,
    deleteGoal,
  }
}

export interface CoachFeedbackGoal {
  readonly id: string
  readonly createdAt: string
  readonly feedbackTopic: CoachFeedbackGoalTopic
  readonly isActive: boolean
  readonly perspectiveTargets: ReadonlyArray<{
    readonly perspectiveKey: string
    readonly target: number
  }>
  readonly coachees: ReadonlyArray<{
    readonly coachee: Member
    readonly lastDialogConductedAt: string | null
    readonly progress: ReadonlyArray<{
      readonly perspectiveKey: string
      readonly count: number
      readonly target: number
    }>
  }>
}
